import { useHandler } from "@chatbotgang/etude/react/useHandler";

import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";

export type ConversionMapping = InterludeTypes["ConversionMappingResponse"];
export type CreateConversionMappingRequest =
  InterludeTypes["CreateConversionMappingRequest"];
export type UpdateConversionMappingRequest =
  InterludeTypes["UpdateConversionMappingRequest"];

/**
 * Per-account conversion mappings list. The list is disabled while accountId
 * is unset (0 / unselected) — callers that pass `accountId ?? 0` would
 * otherwise fire a 404-bound request against harmony before the user picks
 * an account.
 */
export function useConversionMappings(orgUuid: string, accountId: number) {
  return interlude.googleAds.useListConversionMappings(
    {
      params: {
        orgUuid,
        googleAdsAccountId: accountId,
      },
    },
    {
      enabled: accountId > 0 && orgUuid !== "",
    },
  );
}

interface MutationOptions {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

export function useCreateConversionMapping(
  orgUuid: string,
  accountId: number,
  options: MutationOptions = {},
) {
  const handleSuccess = useHandler(() => {
    options.onSuccess?.();
  });
  const handleError = useHandler((error: unknown) => {
    options.onError?.(error);
  });
  return interlude.googleAds.useCreateConversionMapping(
    {
      params: {
        orgUuid,
        googleAdsAccountId: accountId,
      },
    },
    {
      onSuccess: handleSuccess,
      onError: handleError,
    },
  );
}

export function useUpdateConversionMapping(
  orgUuid: string,
  accountId: number,
  conversionMappingId: number,
  options: MutationOptions = {},
) {
  const handleSuccess = useHandler(() => {
    options.onSuccess?.();
  });
  const handleError = useHandler((error: unknown) => {
    options.onError?.(error);
  });
  return interlude.googleAds.useUpdateConversionMapping(
    {
      params: {
        orgUuid,
        googleAdsAccountId: accountId,
        conversionMappingId,
      },
    },
    {
      onSuccess: handleSuccess,
      onError: handleError,
    },
  );
}

export function useDeleteConversionMapping(
  orgUuid: string,
  accountId: number,
  conversionMappingId: number,
  options: MutationOptions = {},
) {
  const handleSuccess = useHandler(() => {
    options.onSuccess?.();
  });
  const handleError = useHandler((error: unknown) => {
    options.onError?.(error);
  });
  return interlude.googleAds.useDeleteConversionMapping(
    {
      params: {
        orgUuid,
        googleAdsAccountId: accountId,
        conversionMappingId,
      },
    },
    {
      onSuccess: handleSuccess,
      onError: handleError,
    },
  );
}
